import React, { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { Toaster } from '@/components/ui/sonner';
import { toast } from 'sonner';
import Login from '@/pages/Login';
import ForgotPassword from '@/pages/ForgotPassword';
import ResetPassword from '@/pages/ResetPassword';
import Dashboard from '@/pages/Dashboard';
import CustomersList from '@/pages/CustomersList';
import HierarchyTree from '@/pages/HierarchyTree';
import CustomerForm from '@/pages/CustomerForm';
import CustomerDetail from '@/pages/CustomerDetail';
import CircuitSearch from '@/pages/CircuitSearch';
import MapView from '@/pages/MapView';
import Quotations from '@/pages/Quotations';
import SalesOrders from '@/pages/SalesOrders';
import LcoPackages from '@/pages/LcoPackages';
import PurchaseOrders from '@/pages/PurchaseOrders';
import Employees from '@/pages/Employees';
import EmployeeDirectory from '@/pages/hr/EmployeeDirectory';
import Settings from '@/pages/Settings';
import TicketsList from '@/pages/TicketsList';
import TicketDetail from '@/pages/TicketDetail';
import TicketAnalytics from '@/pages/TicketAnalytics';
import ShiftRoster from '@/pages/ShiftRoster';
import NewTicket from '@/pages/NewTicket';
import Agreements from '@/pages/Agreements';
import WhatsAppInbox from '@/pages/WhatsAppInbox';
import GSTReports from '@/pages/GSTReports';
import Accounting from '@/pages/Accounting';
import ReportsPortal from '@/pages/ReportsPortal';
import DNSManagement from '@/pages/DNSManagement';
import OTTManagement from '@/pages/OTTManagement';
import IPTVPackages from '@/pages/IPTVPackages';
import IPAM from '@/pages/IPAM';
import Tasks from '@/pages/Tasks';
import FileManager from '@/pages/FileManager';
import Payments from '@/pages/Payments';
import Railtel from '@/pages/Railtel';
import PartnerAPIConsole from '@/pages/PartnerAPIConsole';
import PortalOTPInbox from '@/pages/PortalOTPInbox';
import PartnerRailtel from '@/pages/portal/PartnerRailtel';
import Layout from '@/components/Layout';
import PWAInstallPrompt from '@/components/PWAInstallPrompt';
import TaskReminderPopup from '@/components/TaskReminderPopup';
import { registerServiceWorker, addNetworkListeners } from '@/utils/serviceWorker';

// Portal Pages
import PortalLogin from '@/pages/portal/PortalLogin';
import CareLogin from '@/pages/care/CareLogin';
import CarePortal from '@/pages/care/CarePortal';
import CarePricing from '@/pages/portal/CarePricing';
import CareSettlements from '@/pages/accounting/CareSettlements';
import PartnerPortalLogin from '@/pages/partner-portal/PartnerPortalLogin';
import PartnerPortalShell from '@/pages/partner-portal/PartnerPortalShell';
import PortalDashboard from '@/pages/portal/PortalDashboard';
import PortalNewTicket from '@/pages/portal/PortalNewTicket';
import PortalTicketDetail from '@/pages/portal/PortalTicketDetail';
import PortalWallet from '@/pages/portal/PortalWallet';
import PartnerSignup from '@/pages/portal/PartnerSignup';
import PartnerLCOManagement from '@/pages/portal/PartnerLCOManagement';
import PartnerIPTV from '@/pages/portal/PartnerIPTV';
import PartnerCommissions from '@/pages/portal/PartnerCommissions';

function PrivateRoute({ children }) {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-slate-600">Loading...</div>
      </div>
    );
  }
  
  return user ? children : <Navigate to="/login" />;
}

function AppRoutes() {
  const { user } = useAuth();
  
  return (
    <Routes>
      <Route path="/login" element={user ? <Navigate to="/" /> : <Login />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      
      {/* Dashboard */}
      <Route path="/" element={
        <PrivateRoute>
          <Layout>
            <Dashboard />
          </Layout>
        </PrivateRoute>
      } />
      
      {/* Unified Customers Routes */}
      <Route path="/customers" element={
        <PrivateRoute>
          <Layout>
            <CustomersList />
          </Layout>
        </PrivateRoute>
      } />
      <Route path="/customers/new" element={
        <PrivateRoute>
          <Layout>
            <CustomerForm />
          </Layout>
        </PrivateRoute>
      } />
      <Route path="/customers/:customer_id" element={
        <PrivateRoute>
          <Layout>
            <CustomerDetail />
          </Layout>
        </PrivateRoute>
      } />
      <Route path="/hierarchy" element={
        <PrivateRoute>
          <Layout>
            <HierarchyTree />
          </Layout>
        </PrivateRoute>
      } />
      
      {/* Circuit Search */}
      <Route path="/circuits" element={
        <PrivateRoute>
          <Layout>
            <CircuitSearch />
          </Layout>
        </PrivateRoute>
      } />
      
      {/* Map View */}
      <Route path="/map" element={
        <PrivateRoute>
          <Layout>
            <MapView />
          </Layout>
        </PrivateRoute>
      } />

      {/* HR - Employee Directory */}
      <Route path="/hr" element={
        <PrivateRoute>
          <Layout>
            <EmployeeDirectory />
          </Layout>
        </PrivateRoute>
      } />
      
      {/* Quotations */}
      <Route path="/quotations" element={
        <PrivateRoute>
          <Layout>
            <Quotations />
          </Layout>
        </PrivateRoute>
      } />
      
      <Route path="/sales-orders" element={
        <PrivateRoute>
          <Layout>
            <SalesOrders />
          </Layout>
        </PrivateRoute>
      } />
      <Route path="/lco-packages" element={
        <PrivateRoute>
          <Layout>
            <LcoPackages />
          </Layout>
        </PrivateRoute>
      } />

      {/* Purchase Orders - Admin/Accounts */}
      <Route path="/purchase-orders" element={
        <PrivateRoute>
          <Layout>
            <PurchaseOrders />
          </Layout>
        </PrivateRoute>
      } />
      
      {/* Employees - Admin Only */}
      <Route path="/employees" element={
        <PrivateRoute>
          <Layout>
            <Employees />
          </Layout>
        </PrivateRoute>
      } />
      
      {/* Settings - Admin Only */}
      <Route path="/settings" element={
        <PrivateRoute>
          <Layout>
            <Settings />
          </Layout>
        </PrivateRoute>
      } />
      
      {/* Support Tickets - Staff */}
      <Route path="/tickets" element={
        <PrivateRoute>
          <Layout>
            <TicketsList />
          </Layout>
        </PrivateRoute>
      } />
      <Route path="/tickets/:ticket_id" element={
        <PrivateRoute>
          <Layout>
            <TicketDetail />
          </Layout>
        </PrivateRoute>
      } />
      <Route path="/tickets/new" element={
        <PrivateRoute>
          <Layout>
            <NewTicket />
          </Layout>
        </PrivateRoute>
      } />
      <Route path="/tickets/analytics" element={
        <PrivateRoute>
          <Layout>
            <TicketAnalytics />
          </Layout>
        </PrivateRoute>
      } />
      <Route path="/shift-roster" element={
        <PrivateRoute>
          <Layout>
            <ShiftRoster />
          </Layout>
        </PrivateRoute>
      } />
      <Route path="/agreements" element={
        <PrivateRoute>
          <Layout>
            <Agreements />
          </Layout>
        </PrivateRoute>
      } />
      <Route path="/whatsapp" element={
        <PrivateRoute>
          <Layout>
            <WhatsAppInbox />
          </Layout>
        </PrivateRoute>
      } />
      <Route path="/gst-reports" element={
        <PrivateRoute>
          <Layout>
            <GSTReports />
          </Layout>
        </PrivateRoute>
      } />
      <Route path="/accounting" element={
        <PrivateRoute>
          <Layout>
            <Accounting />
          </Layout>
        </PrivateRoute>
      } />
      <Route path="/accounting/care-settlements" element={
        <PrivateRoute>
          <Layout>
            <CareSettlements />
          </Layout>
        </PrivateRoute>
      } />
      <Route path="/dns" element={
        <PrivateRoute>
          <Layout>
            <DNSManagement />
          </Layout>
        </PrivateRoute>
      } />
      <Route path="/ott" element={
        <PrivateRoute>
          <Layout>
            <OTTManagement />
          </Layout>
        </PrivateRoute>
      } />
      <Route path="/iptv" element={
        <PrivateRoute>
          <Layout>
            <IPTVPackages />
          </Layout>
        </PrivateRoute>
      } />
      <Route path="/ipam" element={
        <PrivateRoute>
          <Layout>
            <IPAM />
          </Layout>
        </PrivateRoute>
      } />
      <Route path="/tasks" element={
        <PrivateRoute>
          <Layout>
            <Tasks />
          </Layout>
        </PrivateRoute>
      } />
      <Route path="/files" element={
        <PrivateRoute>
          <Layout>
            <FileManager />
          </Layout>
        </PrivateRoute>
      } />
      <Route path="/payments" element={
        <PrivateRoute>
          <Layout>
            <Payments />
          </Layout>
        </PrivateRoute>
      } />
      <Route path="/railtel" element={
        <PrivateRoute>
          <Layout>
            <Railtel />
          </Layout>
        </PrivateRoute>
      } />
      <Route path="/partner-api" element={
        <PrivateRoute>
          <Layout>
            <PartnerAPIConsole />
          </Layout>
        </PrivateRoute>
      } />
      <Route path="/portal-otp-inbox" element={
        <PrivateRoute>
          <Layout>
            <PortalOTPInbox />
          </Layout>
        </PrivateRoute>
      } />
      <Route path="/partner/railtel" element={<PartnerRailtel />} />
      <Route path="/portal/railtel" element={<PartnerRailtel />} />
      
      {/* Reports Portal (Separate Restricted Login) */}
      <Route path="/reports-login" element={<ReportsPortal />} />
      <Route path="/reports/*" element={<ReportsPortal />} />

      {/* Partner Portal (External Watcho API partners — Playbox etc.) */}
      <Route path="/partner-portal/login" element={<PartnerPortalLogin />} />
      <Route path="/partner-portal" element={<PartnerPortalShell />} />
      <Route path="/partner-portal/dashboard" element={<PartnerPortalShell />} />
      <Route path="/partner-portal/activations" element={<PartnerPortalShell />} />
      <Route path="/partner-portal/customers" element={<PartnerPortalShell />} />
      <Route path="/partner-portal/wallet" element={<PartnerPortalShell />} />
      
      {/* Customer Portal (Public/Separate Auth) */}
      <Route path="/portal" element={<PortalLogin />} />
      <Route path="/portal/dashboard" element={<PortalDashboard />} />
      <Route path="/portal/tickets" element={<PortalDashboard />} />
      <Route path="/portal/tickets/new" element={<PortalNewTicket />} />
      <Route path="/portal/tickets/:ticket_id" element={<PortalTicketDetail />} />
      <Route path="/portal/wallet" element={<PortalWallet />} />
      
      {/* Partner Portal (Alias for Customer Portal) */}
      <Route path="/partner" element={<PortalLogin />} />
      <Route path="/partner/signup" element={<PartnerSignup />} />
      <Route path="/partner/dashboard" element={<PortalDashboard />} />
      <Route path="/partner/tickets" element={<PortalDashboard />} />
      <Route path="/partner/tickets/new" element={<PortalNewTicket />} />
      <Route path="/partner/tickets/:ticket_id" element={<PortalTicketDetail />} />
      <Route path="/partner/wallet" element={<PortalWallet />} />
      <Route path="/partner/lcos" element={<PartnerLCOManagement />} />
      <Route path="/partner/iptv" element={<PartnerIPTV />} />
      <Route path="/partner/commissions" element={<PartnerCommissions />} />
      
      {/* Customer Portal LCO route */}
      <Route path="/portal/lcos" element={<PartnerLCOManagement />} />
      <Route path="/portal/iptv" element={<PartnerIPTV />} />
      <Route path="/portal/care-pricing" element={<CarePricing />} />
      <Route path="/partner/care-pricing" element={<CarePricing />} />
      <Route path="/portal/commissions" element={<PartnerCommissions />} />

      {/* ANT TV Subscriber Self-Care Portal (public, mobile+OTP) */}
      <Route path="/care" element={<CareLogin />} />
      <Route path="/care/home" element={<CarePortal />} />
      <Route path="/care/payments" element={<CarePortal />} />
      <Route path="/care/profile" element={<CarePortal />} />
      <Route path="/care/payment/success" element={<CarePortal />} />
      <Route path="/care/payment/failure" element={<CarePortal />} />
      
      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
  );
}

function App() {
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    // Register service worker
    registerServiceWorker();

    // Network status listeners
    const cleanup = addNetworkListeners(
      () => {
        setIsOnline(true);
        toast.success('Back online', { duration: 2000 });
      },
      () => {
        setIsOnline(false);
        toast.warning('You are offline', { 
          description: 'Some features may be limited',
          duration: 4000 
        });
      }
    );

    return cleanup;
  }, []);

  return (
    <AuthProvider>
      <BrowserRouter>
        {!isOnline && (
          <div className="fixed top-0 left-0 right-0 z-50 bg-amber-500 text-white text-center py-1 text-sm font-medium">
            You're offline — Some features may be unavailable
          </div>
        )}
        <AppRoutes />
        <TaskReminderPopup />
        <Toaster position="top-right" />
        <PWAInstallPrompt />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
